import React from 'react'
import PageLayout from 'layouts/PageLayout'
import EmptyHeader from 'containers/EmptyHeader'
import Footer from 'components/Footer'
import ErrorMessage from 'components/ErrorMessage'
import Button from 'components/Button'
import { ReactComponent as LockIcon } from 'icons/lock-64.svg'
import { useGetWorkspacesQuery } from 'gqlTypes'
import { DynamicLoadingPage } from 'containers/DynamicLoadingPage'
import GenericErrorView from 'views/GenericErrorView'
import { useSignInToUnauthorizedWorkspace } from 'modules/sso/operations'
import useUserProfile from 'modules/user/useUserProfile'
import { CenterContent } from 'containers/CenterContent'

interface WrongAuthorizationViewProps {
  workspaceId?: string
}

const Description = ({ hasWorkspace }: { hasWorkspace: boolean }) => {
  return hasWorkspace ? (
    <>
      This document is part of another workspace that you are already a member
      of. <br />
      Please sign in to view this document
    </>
  ) : (
    <>
      To access this document, you need to log into the account that has access
      to it.
    </>
  )
}

export const WrongAuthorizationView = ({
  workspaceId,
}: WrongAuthorizationViewProps) => {
  /**
   * Skipping this query will make all data/error and loading values to be
   * undefined/false.
   */
  const { data, loading, error } = useGetWorkspacesQuery({ skip: !workspaceId })
  const { signIn, signInToPersonalAccount } = useSignInToUnauthorizedWorkspace()
  const { data: userData } = useUserProfile()

  if (loading) {
    return <DynamicLoadingPage />
  }

  if (error) {
    return <GenericErrorView />
  }

  const workspace = data?.me?.workspaces?.find(
    workspace => workspace?.identifier === workspaceId
  )

  const user = {
    name: userData?.me?.name ?? '',
    email: userData?.me?.email ?? '',
    avatarSrc: userData?.me?.avatar?.small ?? '',
  }

  return (
    <PageLayout header={<EmptyHeader />} footer={<Footer />}>
      <CenterContent>
        <ErrorMessage.Generic
          title="Sign in to view this document"
          icon={<LockIcon />}
          iconSize="large"
          extra={
            <Button
              variant="primary"
              onClick={() => {
                if (workspace) {
                  signIn(
                    {
                      workspaceId: workspace.identifier,
                      workspaceName: workspace.name,
                      ssoEnabled: workspace.customer?.ssoEnabled || false,
                      ssoStartUrl: workspace.customer?.ssoStartUrl || undefined,
                    },
                    user,
                    true
                  )
                } else {
                  signInToPersonalAccount({
                    workspaceName: 'Personal',
                    user,
                    onCompleted: () => {},
                  })
                }
              }}>
              Sign In
            </Button>
          }>
          <Description hasWorkspace={true} />
        </ErrorMessage.Generic>
      </CenterContent>
    </PageLayout>
  )
}
